import { useIntersectionObserver } from '@td-client-frontend/common';
import React, { useMemo, RefObject, useEffect, useState, useCallback } from 'react';

import { PiwikName } from '../@types/common';
import { Group, GroupType } from '../api/api';
import { Td } from '../components/Td';
import ListItem from '../components/ListItem_old';
import { PermMode } from '../components/WithPermission';
import { useAuth } from '../context/AuthContext';
import { addTrackEvent, trackFilterEvents } from '../utils/piwikUtils';
import { useDraftQuery } from './useDraftQuery';
import { useFormData } from './old_useFormData';
import { GroupSearchPayload, useGroupQuery } from './useGroupQuery';

interface Props extends GroupSearchPayload {
  loadMoreRef?: RefObject<HTMLSpanElement>;
}

function useGroupList({ search, loadMoreRef }: Partial<Props>) {
  const { checkPerm } = useAuth();
  const [selectedId, setSelectedId] = useState<string>('');
  // @ts-ignore
  const [type, setType] = useState<GroupType>(() => (checkPerm(['GroupRead'], PermMode.ALL) ? 'active' : 'draft'));

  const onFilterChange = useCallback(
    (newFilter: GroupType) => {
      if (type !== newFilter) {
        trackFilterEvents(newFilter);
        setType(newFilter);
        setSelectedId('');
      }
    },
    [type]
  );

  const onGroupSelect = (id: string) => {
    addTrackEvent(PiwikName.MANAGE_DRAFT);
    setSelectedId(id);
  };

  const { values: groupValues, groupStatus } = useGroupQuery({
    type,
    search
  });

  const {
    values: draftValues,
    draftStatus,
    isFetchingMore,
    canFetchMore,
    fetchMore
  } = useDraftQuery({
    type,
    search
  });

  useIntersectionObserver({
    target: loadMoreRef,
    onIntersect: fetchMore,
    enabled: canFetchMore
  });

  const values: Group[] = type === 'draft' && Array.isArray(draftValues) ? draftValues[0]?.results : groupValues || [];
  useEffect(() => {
    if (values?.length) {
      setSelectedId(values[0]._id);
    }
  }, [values]);

  const listItems = useMemo(() => {
    return values.map(
      ({ name, planNumber, _id }) => {
        return (
          <ListItem _id={_id} key={_id} selected={selectedId} onClick={() => onGroupSelect(_id)}>
            <Td>{name}</Td>
            <Td>{planNumber}</Td>
          </ListItem>
        );
      },
      [selectedId, values]
    );
  }, [selectedId, values]);

  const form = useFormData({
    type,
    id: selectedId
  });

  return {
    listItems,
    status: type === 'draft' ? draftStatus : groupStatus,
    selectedId,
    filter: type,
    onFilterChange,
    fetchMore,
    isFetchingMore,
    canFetchMore,
    form
  };
}

export default useGroupList;
